import { DragDropModule } from '@angular/cdk/drag-drop';
import { OverlayModule } from '@angular/cdk/overlay';
import { CommonModule, NgClass } from '@angular/common';
import { HttpClientModule } from '@angular/common/http';
import { NgModule } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { RouterLink, RouterLinkActive } from '@angular/router';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';

import { ScoreboardComponent } from '@modules/competition/step-parameterization/group-key/scoreboard/scoreboard.component';
import { ModalAddParticipantsComponent } from '@modules/competition/step-parameterization/modal-add-participants/modal-add-participants.component';

import { NgLeafletUniversalModule } from 'ng-leaflet-universal';

// COMPONENTS
import { AccordionComponent } from './components/accordion/accordion.component';
import { ActionButtonsComponent } from './components/action-buttons/action-buttons.component';
import { BoxInfoComponent } from './components/box-info/box-info.component';
import { ButtonComponent } from './components/button/button.component';
import { ChipTagComponent } from './components/chip-tag/chip-tag.component';
import { CircleLoadingComponent } from './components/circle-loading/circle-loading.component';
import { ClashBoxComponent } from './components/clash-box/clash-box.component';
import { ColorPickerComponent } from './components/color-picker/color-picker.component';
import { ConfirmationModalComponent } from './components/confirmation-modal/confirmation-modal.component';
import { ContentContainerComponent } from './components/content-container/content-container.component';
import { DataCardComponent } from './components/dashboard/data-card/data-card.component';
import { DateTimePickerComponent } from './components/date-time-picker/date-time-picker.component';
import { DragDropComponent } from './components/drag-drop/drag-drop.component';
import { DropdownComponent } from './components/dropdown/dropdown.component';
import { NewDropdownComponent } from './components/dropdown/new-dropdown.component';
import { ErrorComponent } from './components/error/error.component';
import { FileUploadComponent } from './components/file-upload/file-upload.component';
import { FiltersIndicatorComponent } from './components/filter-indicator/filter-indicator';
import { FooterComponent } from './components/footer/footer.component';
import { HeaderComponent } from './components/header/header.component';
import { InputContainerComponent } from './components/input-container/input-container.component';
import { InputSearchNewComponent } from './components/input-search/input-search-new.component';
import { InputSearchComponent } from './components/input-search/input-search.component';
import { ItemSelectedListComponent } from './components/item-selected-list/item-selected-list.component';
import { ItemSelectorComponent } from './components/item-selected-list/item-selector.component';
import { ListContainerComponent } from './components/list-container/list-container.component';
import { ListDividerComponent } from './components/list/list-divider/list-divider.component';
import { ListInfoComponent } from './components/list/list-info/list-info.component';
import { ListItemComponent } from './components/list/list-item/list-item.component';
import { NewListItemComponent } from './components/list/list-item/new-list-item.component';
import { ListTitleComponent } from './components/list/list-title/list-title.component';
import { ListComponent } from './components/list/list.component';
import { LoadingComponent } from './components/loading/loading.component';
import { MapComponent } from './components/map/map-component';
import { MarqueeComponent } from './components/marquee/marquee.component';
import { MediaDisplayerModalComponent } from './components/media-displayer-modal/media-displayer-modal.component';
import { MenuBarItemComponent } from './components/menu-bar/menu-bar-item/menu-bar-item.component';
import { MenuBarComponent } from './components/menu-bar/menu-bar.component';
import { MessageComponent } from './components/message/message.component';
import { ModalComponent } from './components/modal/modal.component';
import { NewModalComponent } from './components/modal/new-modal.component';
import { MultiselectSearchComponent } from './components/multiselect-search/multiselect-search.component';
import { MultiselectComponent } from './components/multiselect/multiselect.component';
import { NotFoundMessageListComponent } from './components/not-found-message-list/not-found-message-list.component';
import { OffcanvasComponent } from './components/offcanvas/offcanvas.component';
import { OutlinedBoxComponent } from './components/outlined-box/outlined-box.component';
import { PagedDropdownComponent } from './components/paged-dropdown/paged-dropdown.component';
import { PopoverComponent } from './components/popover/popover.component';
import { PaginationListComponent } from './components/pagination-list/pagination-list.component';
import { PanelBoxComponent } from './components/panel-box/panel-box.component';
import { ProgressBarComponent } from './components/progress-bar.component/progress-bar.component';
import { RadioButtonComponent } from './components/radio-button/radio-button.component';
import { SelectSearchContainerComponent } from './components/select-search-container/select-search-container.component';
import { SelectedCardComponent } from './components/selected-card/selected-card.component';
import { SetScoreboardComponent } from './components/set-scoreboard/set-scoreboard.component';
import { SideNavComponent } from './components/side-nav/side-nav.component';
import { SnackbarComponent } from './components/snackbar/snackbar.component';
import { StatusBadgeComponent } from './components/status-badge/status-badge.component';
import { StepComponent } from './components/stepper/step/step.component';
import { StepperComponent } from './components/stepper/stepper.component';
import { SwitchComponent } from './components/switch/switch.component';
import { TabsComponent } from './components/tabs/tabs.component';
import { ToggleButtonComponent } from './components/toggle-button/toggle-button.component';
import { TopListComponent } from './components/top-list/top-list.component';
import { TotalRecordsListComponent } from './components/total-records-list/total-records-list.component';

// DIRECTIVES
import { BirthdateValidatorDirective } from './directives/birthdate-validator.directive';
import { ClickOutsideDirective } from './directives/click-outside.directive';
import { DebouncedInputDirective } from './directives/debounced-input.directive';
import { InputDirective } from './directives/input.directive';
import { ListTypeInjectDirective } from './directives/list-type-inject.directive';
import { PanDirective } from './directives/pan.directive';
import { PreventEnterSubmitDirective } from './directives/prevent-enter-submit.directive';
import { ThrottleClickDirective } from './directives/throttle-click.directive';
import { BlockNumbersDirective } from './directives/block-numbers.directive';

// PIPES
import { DatetimeFormatPipe, HourMaskPipe } from './pipes/datetime-format.pipe';
import { FormatEnumPipe } from './pipes/format-enum.pipe';

// EXTRA COMPONENTS
import { CardComponent } from './components/roulette/card/card.component';
import { RouletteComponent } from './components/roulette/roulette.component';
import { CaptalizePipe } from './pipes/captalize.pipe';
import { GenderPipe } from './pipes/naipe.pipe';
import { RemoveGenderFromTextPipe } from './pipes/remove-gender-from-text.pipe';
import { RadioToggleComponent } from './radio-toggle/radio-toggle.component';
import { KeyGridComponent } from './components/key-grid/key-grid.component';
import { CheckboxComponent } from './components/checkbox/checkbox.component';

@NgModule({
  declarations: [
    // Components
    AccordionComponent,
    ActionButtonsComponent,
    BoxInfoComponent,
    ButtonComponent,
    ChipTagComponent,
    CircleLoadingComponent,
    ClashBoxComponent,
    ConfirmationModalComponent,
    ContentContainerComponent,
    DateTimePickerComponent,
    DragDropComponent,
    DropdownComponent,
    NewDropdownComponent,
    ErrorComponent,
    FileUploadComponent,
    FiltersIndicatorComponent,
    FooterComponent,
    HeaderComponent,
    InputContainerComponent,
    InputSearchComponent,
    InputSearchNewComponent,
    ItemSelectedListComponent,
    ItemSelectorComponent,
    ListContainerComponent,
    ListDividerComponent,
    ListInfoComponent,
    ListItemComponent,
    NewListItemComponent,
    ListTitleComponent,
    ListComponent,
    LoadingComponent,
    MapComponent,
    MarqueeComponent,
    MediaDisplayerModalComponent,
    MenuBarComponent,
    MenuBarItemComponent,
    MessageComponent,
    ModalComponent,
    NewModalComponent,
    ModalAddParticipantsComponent,
    MultiselectComponent,
    MultiselectSearchComponent,
    NewModalComponent,
    NotFoundMessageListComponent,
    OffcanvasComponent,
    OutlinedBoxComponent,
    PagedDropdownComponent,
    PaginationListComponent,
    PanelBoxComponent,
    ProgressBarComponent,
    RadioButtonComponent,
    RadioToggleComponent,
    ScoreboardComponent,
    PopoverComponent,
    SelectSearchContainerComponent,
    SelectedCardComponent,
    SetScoreboardComponent,
    SideNavComponent,
    SnackbarComponent,
    StepperComponent,
    StepComponent,
    SwitchComponent,
    TabsComponent,
    ToggleButtonComponent,
    TopListComponent,
    TotalRecordsListComponent,
    ColorPickerComponent,
    DataCardComponent,
    StatusBadgeComponent,
    CheckboxComponent,

    // Directives
    BirthdateValidatorDirective,
    ClickOutsideDirective,
    DebouncedInputDirective,
    InputDirective,
    ListTypeInjectDirective,
    PanDirective,
    PreventEnterSubmitDirective,
    ThrottleClickDirective,
    BlockNumbersDirective,
    // Pipes
    RemoveGenderFromTextPipe,
    CaptalizePipe,
    GenderPipe,
    DatetimeFormatPipe,
    HourMaskPipe,
    FormatEnumPipe,
    RouletteComponent,
    CardComponent,
    KeyGridComponent,
  ],
  imports: [
    CommonModule,
    DragDropModule,
    OverlayModule,
    HttpClientModule,
    RouterLink,
    RouterLinkActive,
    FormsModule,
    ReactiveFormsModule,
    FontAwesomeModule,
    NgClass,
    NgLeafletUniversalModule,
  ],
  exports: [
    // Export only once each
    AccordionComponent,
    ActionButtonsComponent,
    BoxInfoComponent,
    ButtonComponent,
    ChipTagComponent,
    CircleLoadingComponent,
    ClashBoxComponent,
    ConfirmationModalComponent,
    ContentContainerComponent,
    DateTimePickerComponent,
    DragDropComponent,
    DropdownComponent,
    NewDropdownComponent,
    ErrorComponent,
    FileUploadComponent,
    FiltersIndicatorComponent,
    FooterComponent,
    HeaderComponent,
    InputContainerComponent,
    InputSearchComponent,
    InputSearchNewComponent,
    ItemSelectedListComponent,
    ItemSelectorComponent,
    ListContainerComponent,
    ListDividerComponent,
    ListInfoComponent,
    ListItemComponent,
    NewListItemComponent,
    ListTitleComponent,
    ListComponent,
    LoadingComponent,
    MapComponent,
    MarqueeComponent,
    MediaDisplayerModalComponent,
    MenuBarComponent,
    MenuBarItemComponent,
    MessageComponent,
    ModalComponent,
    NewModalComponent,
    ModalAddParticipantsComponent,
    MultiselectComponent,
    MultiselectSearchComponent,
    NewModalComponent,
    NotFoundMessageListComponent,
    OffcanvasComponent,
    OutlinedBoxComponent,
    PagedDropdownComponent,
    PaginationListComponent,
    PanelBoxComponent,
    ProgressBarComponent,
    RadioButtonComponent,
    RadioToggleComponent,
    ScoreboardComponent,
    PopoverComponent,
    SelectSearchContainerComponent,
    SelectedCardComponent,
    SetScoreboardComponent,
    SideNavComponent,
    SnackbarComponent,
    StepperComponent,
    StepComponent,
    SwitchComponent,
    TabsComponent,
    ToggleButtonComponent,
    TopListComponent,
    TotalRecordsListComponent,
    ColorPickerComponent,
    DataCardComponent,
    StatusBadgeComponent,
    RouletteComponent,
    KeyGridComponent,
    CheckboxComponent,

    // Directives
    BirthdateValidatorDirective,
    ClickOutsideDirective,
    DebouncedInputDirective,
    InputDirective,
    ListTypeInjectDirective,
    PanDirective,
    PreventEnterSubmitDirective,
    ThrottleClickDirective,
    BlockNumbersDirective,

    // Pipes
    DatetimeFormatPipe,
    HourMaskPipe,
    FormatEnumPipe,
    RemoveGenderFromTextPipe,
    CaptalizePipe,
    GenderPipe,

    // Angular modules
    CommonModule,
    DragDropModule,
    HttpClientModule,
    RouterLink,
    RouterLinkActive,
    FormsModule,
    ReactiveFormsModule,
    FontAwesomeModule,
    NgClass,
    NgLeafletUniversalModule,
  ],
})
export class SharedModule {}
